import React, { useState, useRef, useEffect } from 'react';
import './evidence-preview.styles.scss';

const ALIGN_TRANSFORM = {
  left: undefined,
  center: 'translateX(-50%)',
  right: 'translateX(-100%)',
};

const getDims = (orientation) =>
  orientation === 'landscape'
    ? { w: 1056, h: 816 }   // 11 × 8.5 in at 96 DPI
    : { w: 816,  h: 1056 }; // 8.5 × 11 in at 96 DPI

/**
 * Renders one or more template pages with variable text overlaid at authored positions.
 * The canvas is always rendered at the correct print dimensions and CSS-scaled to fit the
 * available container width, so on-screen appearance matches print output exactly.
 *
 * Print-ready via window.print() — injects the correct @page rule automatically via
 * the beforeprint event so portrait/landscape is honoured without any browser setting changes.
 *
 * @param {Array}  pages       - [{ id, bgImage, fields }]
 * @param {Object} data        - { userName, startDate, endDate, title, presenters, hours, userId }
 * @param {string} orientation - 'portrait' (default) | 'landscape'
 */
const EvidencePreview = ({ pages = [], data = {}, orientation = 'portrait' }) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const { w: printW, h: printH } = getDims(orientation);

  // Scale canvas to fit the container, updating on resize or orientation change.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (containerRef.current) setScale(containerRef.current.offsetWidth / printW);
    });
    ro.observe(el);
    setScale(el.offsetWidth / printW);
    return () => ro.disconnect();
  }, [printW]);

  // Inject the correct @page rule before the browser opens the print dialog.
  useEffect(() => {
    const handleBeforePrint = () => {
      let el = document.getElementById('ec-page-rule');
      if (!el) {
        el = document.createElement('style');
        el.id = 'ec-page-rule';
        document.head.appendChild(el);
      }
      el.textContent = orientation === 'landscape'
        ? '@page { size: 11in 8.5in landscape; margin: 0; }'
        : '@page { size: 8.5in 11in portrait;  margin: 0; }';
    };
    window.addEventListener('beforeprint', handleBeforePrint);
    return () => window.removeEventListener('beforeprint', handleBeforePrint);
  }, [orientation]);

  const visiblePages = pages.filter((p) => p.bgImage);

  return (
    <div id="ec-print-canvas" className="ec-print-canvas" ref={containerRef}>
      {visiblePages.map((page, index) => (
        <div
          key={page.id}
          className={`ec-print-page${index < visiblePages.length - 1 ? ' ec-page-break' : ''}`}
          style={{ height: printH * scale }}
        >
          <div
            className="ec-print-bg"
            style={{
              width: printW,
              height: printH,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          >
            <img src={page.bgImage} className="ec-bg-img" alt="" />
            {page.fields.map((field) => (
              <div
                key={field.id}
                className="ec-print-field"
                style={{
                  left: `${field.x}%`,
                  top: `${field.y}%`,
                  fontSize: field.fontSize,
                  color: field.color,
                  fontWeight: field.bold ? 'bold' : 'normal',
                  fontStyle: field.italic ? 'italic' : 'normal',
                  transform: ALIGN_TRANSFORM[field.align],
                }}
              >
                {data[field.variable] ?? ''}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EvidencePreview;
